import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, relTime } from '../lib/api'

/**
 * The platform-wide audit trail — every mutating action, every tenant, newest first. The
 * operator's "see everything" surface: plan changes, domain/key/IP mutations, admin overrides,
 * each with actor, IP and reason. Filter by action prefix (e.g. "tenant." or "sending_ip.").
 */
const PAGE = 50

export function AdminAudit() {
  const [action, setAction] = useState('')
  const [page, setPage] = useState(0)
  // Reset to the first page whenever the filter changes, so the offset can't point past the
  // (now smaller) filtered result set.
  const onFilter = (v: string) => { setAction(v); setPage(0) }
  const { data, isLoading } = useQuery({
    queryKey: ['admin-audit', action, page],
    queryFn: () => api.admin.audit({ action: action || undefined, limit: PAGE, offset: page * PAGE }),
  })
  const rows = data?.data ?? []
  const total = data?.total ?? 0
  const from = total === 0 ? 0 : page * PAGE + 1
  const to = Math.min(total, page * PAGE + rows.length)
  const hasPrev = page > 0
  const hasNext = (page + 1) * PAGE < total

  return (
    <div>
      <div className="admin-head">
        <div>
          <span className="op-badge">Operator</span>
          <h1 style={{ marginTop: 8 }}>Audit log</h1>
          <p className="lede" style={{ marginTop: 4 }}>Every mutating action across the platform, newest first.</p>
        </div>
        <span className="small muted">{total.toLocaleString()} entries</span>
      </div>

      <div className="toolbar" style={{ marginBottom: 12 }}>
        <input placeholder="Filter by action prefix — tenant. / domain. / sending_ip. / route." value={action}
               onChange={e => onFilter(e.target.value)} style={{ minWidth: 320, maxWidth: 420 }} />
      </div>

      {isLoading && <div className="skeleton" style={{ height: 240 }} />}
      {!isLoading && rows.length === 0 && <div className="empty"><p>No audit entries match.</p></div>}

      {rows.length > 0 && (
        <div className="card scroll-x">
          <table className="data">
            <thead><tr><th>When</th><th>Actor</th><th>Action</th><th>Entity</th><th>IP</th><th>Reason</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="small muted" title={new Date(r.at).toLocaleString()}>{relTime(r.at)}</td>
                  <td className="small">{r.actor ?? '—'}<div className="muted mono">{r.actor_type}</div></td>
                  <td className="small mono strong">{r.action}</td>
                  <td className="small mono muted">{r.entity_type}{r.entity_id ? `#${r.entity_id}` : ''}</td>
                  <td className="small mono">{r.actor_ip}</td>
                  <td className="small muted">{r.reason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > 0 && (
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <span className="small muted">Showing {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}</span>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn-ghost" disabled={!hasPrev} onClick={() => setPage(p => Math.max(0, p - 1))}>← Prev</button>
            <span className="small muted">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button className="btn-ghost" disabled={!hasNext} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </div>
      )}
    </div>
  )
}
